import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Minus, Plus, Trash2, TrendingDown, TrendingUp } from 'lucide-react';
import {
  AdminPage,
  Button,
  ErrorState,
  Panel,
  Progress,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
  useUndo,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { usePerm } from '@/access/usePerm';
import { useMe } from '@/auth/useMe';
import { useEmployeeOptions, useEmployees } from '@/features/employees/api';
import { formatDate } from '@/lib/dates';
import { formatILS } from '@/lib/money';
import { errorMessage } from '@/lib/errors';
import {
  targetPercent,
  changePercent,
  useEmployeeSalesSummary,
  useBranchSales,
  useRecentSales,
  useSalesMutations,
} from '../api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * הזנת מכירות ודירוג הסניפים (PRD 5.18.2).
 *
 * ההזנה היא סיכום יומי: סכום, ואם רוצים גם מספר עסקאות. שורה בלי
 * עובד היא סיכום הסניף, וזו ברירת המחדל: עד שקומקס יחובר, מכירה
 * לפי מוכר היא עבודה ידנית שאף אחד לא יעשה בפועל.
 */
export function SalesAdminPage() {
  const undo = useUndo();
  const canEnter = usePerm('sales.enter');
  const canView = usePerm('sales.view');
  const { me } = useMe();
  const { data: options } = useEmployeeOptions();
  const { data: employees } = useEmployees();
  const branches = useBranchSales();
  const mutations = useSalesMutations();

  const [branchId, setBranchId] = useState<string | null>(me?.primaryBranchId ?? null);
  const recent = useRecentSales(branchId);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (!canView && !canEnter) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  return (
    <AdminPage title="מכירות" subtitle="הזנה יומית, יעדים ודירוג">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="m-0 text-body-sm text-good">
          {done}
        </p>
      )}

      {canEnter && (
        <Panel title="הזנת מכירות">
          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const formElement = event.currentTarget;
              const form = new FormData(formElement);
              const branch = String(form.get('branch') ?? '');
              const date = String(form.get('date') ?? '');
              const amount = Number(form.get('amount') ?? 0);
              if (!branch || !date || !me?.employeeId) return;
              if (amount < 0) {
                setError('סכום לא יכול להיות שלילי.');
                return;
              }

              const transactions = String(form.get('transactions') ?? '').trim();
              setDone(null);
              run(async () => {
                await mutations.enter.mutateAsync({
                  branch_id: branch,
                  date,
                  employee_id: String(form.get('employee') ?? '') || null,
                  amount,
                  transactions: transactions ? Number(transactions) : null,
                  entered_by: me.employeeId,
                });
                setBranchId(branch);
                setDone(`נשמרו ${formatILS(amount)} ל ${formatDate(date)}.`);
                formElement.reset();
              });
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <label htmlFor="branch" className="text-body-sm text-muted">
                  סניף
                </label>
                <select
                  id="branch"
                  name="branch"
                  defaultValue={me?.primaryBranchId ?? ''}
                  className={FIELD}
                  required
                >
                  <option value="">בחירה</option>
                  {options?.branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-1">
                <label htmlFor="date" className="text-body-sm text-muted">
                  תאריך
                </label>
                <input id="date" name="date" type="date" className={FIELD} required />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="grid gap-1">
                <label htmlFor="amount" className="text-body-sm text-muted">
                  סכום בשקלים
                </label>
                <input
                  id="amount"
                  name="amount"
                  type="number"
                  min={0}
                  step="0.01"
                  className={FIELD}
                  required
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="transactions" className="text-body-sm text-muted">
                  מספר עסקאות
                </label>
                <input id="transactions" name="transactions" type="number" min={0} className={FIELD} />
              </div>
              <div className="grid gap-1">
                <label htmlFor="employee" className="text-body-sm text-muted">
                  על שם מי
                </label>
                <select id="employee" name="employee" className={FIELD}>
                  <option value="">סיכום הסניף</option>
                  {employees
                    ?.filter((employee) => employee.is_active)
                    .map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.first_name} {employee.last_name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div>
              <Button variant="primary" type="submit" disabled={mutations.enter.isPending}>
                <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
                שמירה
              </Button>
            </div>
            <p className="m-0 text-label text-muted">
              הזנה חוזרת לאותו יום ואותו סניף מעדכנת את השורה הקיימת.
            </p>
          </form>
        </Panel>
      )}

      {canView && (
        <Panel title="הסניפים החודש">
          {/*
            ההשוואה היא לפי אותו מספר ימים בכל התקופות (ראו מיגרציה
            043). בלי זה חודש שרץ מושווה לחודש מלא וכל חודש נראה
            כמו קריסה עד היום האחרון שלו.
          */}
          <p className="m-0 text-body-sm text-muted">
            ההשוואות הן לפי אותו מספר ימים בחודש, כדי שחודש שעוד רץ לא ייראה נמוך משהוא.
          </p>
          {branches.isPending && <Skeleton className="h-32" />}
          {branches.isError && <ErrorState onRetry={() => void branches.refetch()} />}
          {branches.data && branches.data.length === 0 && (
            <p className="m-0 text-body-sm text-muted">אין עדיין נתוני מכירות.</p>
          )}
          {branches.data && branches.data.length > 0 && (
            <div className="grid gap-3">
              {branches.data.map((branch, i) => (
                <div key={branch.branch_id} className="grid gap-1 rounded-md border border-line p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="w-6 shrink-0 tabular text-muted">{i + 1}</span>
                    <span className="grow text-body font-medium">{branch.branch_name}</span>
                    <span className="text-title font-medium tabular">
                      {formatILS(branch.this_month)}
                    </span>
                  </div>

                  {branch.target ? (
                    <Progress
                      value={Math.min(branch.this_month, branch.target)}
                      max={branch.target}
                      label={`${targetPercent(branch.this_month, branch.target)}% מהיעד (${formatILS(
                        branch.target,
                      )})`}
                    />
                  ) : (
                    <span className="text-label text-muted">לא הוגדר יעד לחודש הזה</span>
                  )}

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <Change label="מול חודש קודם" value={changePercent(branch.this_month, branch.last_month)} />
                    <Change label="מול אשתקד" value={changePercent(branch.this_month, branch.last_year)} />
                    {branch.best_month ? (
                      <span className="text-label text-muted">שיא: {formatILS(branch.best_month)}</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {canView && <SellersPanel />}

      {canEnter && branchId && (
        <Panel title="השורות האחרונות">
          {recent.isPending && <Skeleton className="h-24" />}
          {recent.data?.length === 0 && (
            <p className="m-0 text-body-sm text-muted">עוד לא הוזנו מכירות לסניף הזה.</p>
          )}
          {recent.data && recent.data.length > 0 && (
            <Table>
              <thead>
                <Tr>
                  <Th>תאריך</Th>
                  <Th>על שם</Th>
                  <Th>סכום</Th>
                  <Th>עסקאות</Th>
                  <Th>מקור</Th>
                  <Th>
                    <span className="sr-only">פעולות</span>
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {recent.data.filter((row) => !undo.isPending(row.id)).map((row) => (
                  <Tr key={row.id}>
                    <Td className="tabular">{formatDate(row.date)}</Td>
                    <Td>
                      {row.employees
                        ? `${row.employees.first_name} ${row.employees.last_name}`
                        : 'הסניף'}
                    </Td>
                    <Td className="tabular">{formatILS(row.amount)}</Td>
                    <Td className="tabular">{row.transactions ?? '—'}</Td>
                    <Td>{row.source === 'comax' ? 'קומקס' : 'ידני'}</Td>
                    <Td>
                      {row.source === 'manual' && (
                        <Button
                          variant="ghost"
                          aria-label="מחיקה"
                          onClick={() => {
                            undo.remove({
                              id: row.id,
                              message: `ההזנה של ${formatDate(row.date)} נמחקה`,
                              perform: () => mutations.remove.mutateAsync(row.id),
                            });
                          }}
                        >
                          <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      )}
    </AdminPage>
  );
}

/**
 * שינוי מול תקופה אחרת. החץ והצבע לעולם לא לבד: המילה והמספר
 * אומרים את אותו דבר (כלל 10).
 */
function Change({ label, value }: { label: string; value: number | null }) {
  if (value === null) {
    return <span className="text-label text-muted">{label}: אין נתון</span>;
  }

  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const tone = value > 0 ? 'text-good' : value < 0 ? 'text-bad' : 'text-muted';

  return (
    <span className={cn('inline-flex items-center gap-1 text-label font-medium', tone)}>
      <Icon size={14} strokeWidth={2} aria-hidden="true" />
      {label}: {value > 0 ? '+' : ''}
      {value}%
    </span>
  );
}

/**
 * אנשי המכירות החודש, לפי אחוז עמידה ביעד.
 *
 * הדירוג לפי אחוז ולא לפי סכום: מוכרת בסניף קטן שעמדה ב 120 אחוז
 * עשתה עבודה טובה יותר ממי שמכר יותר בסניף גדול ולא הגיע ליעד.
 */
function SellersPanel() {
  const employees = useEmployees();
  const sales = useEmployeeSalesSummary();

  const rows = (employees.data ?? [])
    .map((employee) => ({ employee, row: sales.data?.[employee.id] }))
    .filter((entry) => entry.row && (entry.row.sold > 0 || entry.row.target))
    .sort((a, b) => (b.row?.percent ?? -1) - (a.row?.percent ?? -1));

  return (
    <Panel title="אנשי המכירות החודש">
      {(employees.isPending || sales.isPending) && <Skeleton className="h-32" />}

      {!employees.isPending && !sales.isPending && rows.length === 0 && (
        <p className="m-0 text-body-sm text-muted">
          אין מכירות משויכות לעובדים החודש. מכירה נרשמת על שם עובד/ת במסך ההזנה למעלה.
        </p>
      )}

      {rows.length > 0 && (
        <div className="grid gap-2">
          {rows.map(({ employee, row }, i) => (
            <div key={employee.id} className="grid gap-1 rounded-md border border-line p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-6 shrink-0 tabular text-muted">{i + 1}</span>
                <span className="grow text-body font-medium">
                  {employee.first_name} {employee.last_name}
                </span>
                <span className="text-label text-muted">{employee.branches?.name ?? ''}</span>
                <span className="text-title font-medium tabular">{formatILS(row!.sold)}</span>
              </div>

              {row!.target ? (
                <Progress
                  value={Math.min(row!.sold, row!.target)}
                  max={row!.target}
                  label={`${row!.percent}% מהיעד (${formatILS(row!.target)})`}
                />
              ) : (
                <span className="text-label text-muted">לא הוגדר יעד אישי לחודש הזה</span>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <Change label="מול חודש קודם" value={changePercent(row!.sold, row!.last_month)} />
                <Change label="מול אשתקד" value={changePercent(row!.sold, row!.last_year)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
